import type { ComponentType } from 'react';
import { User, Users, Heart, UsersRound, type LucideProps } from 'lucide-react';

type Icon = ComponentType<LucideProps>;

/* ------------------------------------------------------------------ */
/* Audience tags                                                       */
/* ------------------------------------------------------------------ */

export interface AudienceTag {
  label: string;
  Icon: Icon;
}

const SOLO: AudienceTag = { label: 'Solo', Icon: User };
const GROUP: AudienceTag = { label: 'Group', Icon: Users };
const COUPLE: AudienceTag = { label: 'Couple', Icon: Heart };
const FAMILY: AudienceTag = { label: 'Family', Icon: UsersRound };

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

/** A single priced row inside a package group. */
export interface PriceRow {
  /** Number of guests, e.g. '2 people' (tour packages only). */
  guests?: string;
  /** Session length, e.g. '1 hour'. */
  duration?: string;
  /** Formatted price, including any unit, e.g. '€290', '€380 / couple'. */
  price: string;
  /** What this row includes. */
  includes: string;
}

/** A named group of packages (renders one anchor-linkable block). */
export interface PackageGroup {
  /** Anchor id for hash linking, e.g. 'solo-flying-dress-photoshoot'. */
  id: string;
  /** Extra anchor ids that should also scroll to this block. */
  aliases?: string[];
  title: string;
  description?: string;
  rows: PriceRow[];
  /** Free-text note shown in place of (or beneath) a table, e.g. "upon request". */
  note?: string;
}

export interface Experience {
  /** Stable id, e.g. 'flying-dress'. */
  id: string;
  /** LinkTwist product alias used to drive the booking flow, e.g. 'flyingdress'. */
  productAlias: string;
  /** URL slug (no leading slash), e.g. 'flying-dress-photoshoot-santorini'. */
  slug: string;
  /** Short label above the title, e.g. 'Flying Dress'. */
  kicker: string;
  /** Card / nav title, e.g. 'Flying Dress Experience'. */
  title: string;
  /** Full page heading, e.g. 'Santorini Flying Dress Photoshoot'. */
  pageTitle: string;
  /** One-line teaser used on cards. */
  cardDescription: string;
  /** Longer intro paragraph used on the detail page. */
  intro: string;
  /**
   * Gallery images for the experience. The first is the primary/hero image used
   * on cards and page heroes; any extras render in the detail-page gallery.
   */
  images: string[];
  /**
   * Optional override for the card image on the home page only. Cards on the
   * /experiences listing and the detail-page hero still use `images[0]`.
   */
  homeImage?: string;
  badge?: string;
  /** Lowest realistic entry price, used for "from €X" labels. */
  fromPrice: number;
  audience: AudienceTag[];
  /** Bullet list of everything the experience includes. */
  included: string[];
  /** Optional "ideal for" list (Your Love Story). */
  idealFor?: string[];
  packages: PackageGroup[];
  /** Repeated delivery promise shown under the pricing. */
  deliveryNote: string[];
  /** Optional upgrades / add-ons. */
  addOns?: string[];
}

/* ------------------------------------------------------------------ */
/* Shared copy                                                         */
/* ------------------------------------------------------------------ */

const DELIVERY: string[] = [
  'High resolution raw photos delivered in 1 day.',
  'Professionally edited photos delivered in 3 days.',
];

/* ------------------------------------------------------------------ */
/* Experiences                                                         */
/* ------------------------------------------------------------------ */

export const experiences: Experience[] = [
  {
    id: 'flying-dress',
    productAlias: 'flyingdress',
    slug: 'flying-dress-photoshoot-santorini',
    kicker: 'Flying Dress',
    title: 'Flying Dress Experience',
    pageTitle: 'Santorini Flying Dress Photoshoot',
    cardDescription: 'Dramatic, wind-caught gowns above the caldera.',
    intro:
      'A luxury Santorini flying dress photoshoot designed for solo travelers, couples and groups. Choose your flying dress from our full collection and enjoy a private photoshoot with caldera and volcano views, guided by a female photographer and female assistant. Each experience includes refined posing guidance, elegant Santorini locations and fast photo delivery.',
    images: ['/img/flying-dress.webp'],
    homeImage: '/img/flying-dress-home.webp',
    badge: 'Signature',
    fromPrice: 290,
    audience: [SOLO, COUPLE, GROUP],
    included: [
      'Pick-up and drop-off',
      'Female photographer',
      'Female assistant',
      'One flying dress per person',
      'Private dress selection',
      'Optional studio dress changing on site',
      'Posing guidance',
      'Caldera and volcano-view spots',
      'Drinks and bites',
      'High resolution raw photos',
      'Professionally edited photos',
    ],
    packages: [
      {
        id: 'solo-flying-dress-photoshoot',
        title: 'Solo Flying Dress Photoshoot',
        rows: [
          {
            duration: '1 hour',
            price: '€290',
            includes: '120 high resolution raw photos, 30 professionally edited photos, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€390',
            includes: '240 high resolution raw photos, 60 professionally edited photos, 2 reels, 7 spots',
          },
        ],
      },
      {
        id: 'couples-flying-dress-photoshoot',
        title: 'Couples Flying Dress Photoshoot',
        rows: [
          {
            duration: '1 hour',
            price: '€380 / couple',
            includes: '120 high resolution raw photos, 30 professionally edited photos, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€520 / couple',
            includes: '240 high resolution raw photos, 60 professionally edited photos, 2 reels, 7 spots',
          },
        ],
      },
      {
        id: 'group-flying-dress-photoshoot',
        title: 'Group Flying Dress Photoshoot',
        description: 'One flying dress per person, with photos delivered per person.',
        rows: [
          {
            duration: '1 hour',
            price: '€190 / person',
            includes:
              '120 high resolution raw photos per person, 30 professionally edited photos per person, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€260 / person',
            includes:
              '240 high resolution raw photos per person, 60 professionally edited photos per person, 2 reels, 7 spots',
          },
        ],
      },
    ],
    deliveryNote: DELIVERY,
  },
  {
    id: 'casual',
    productAlias: 'photoshoot',
    slug: 'santorini-casual-photoshoot',
    kicker: 'Casual',
    title: 'Casual Photoshoot',
    pageTitle: 'Santorini Casual Photoshoot',
    cardDescription: 'Relaxed, timeless portraits among the blue domes and backstreets.',
    intro:
      'A private Santorini photoshoot for couples, families, solo travelers and groups. This experience is relaxed, elegant and timeless, created for travelers who want beautiful memories without the styling of a flying dress. Enjoy posing guidance, scenic caldera-view locations and a polished lifestyle photography approach.',
    images: ['/img/casual.webp', '/img/casual-2.webp'],
    fromPrice: 230,
    audience: [SOLO, COUPLE, GROUP, FAMILY],
    included: [
      'Pick-up and drop-off',
      'Professional photographer',
      'Posing guidance',
      'Caldera and volcano-view spots',
      'Drinks and bites',
      'High resolution raw photos',
      'Professionally edited photos',
      'Reels included',
    ],
    packages: [
      {
        id: 'solo-casual-photoshoot',
        title: 'Solo Casual Santorini Photoshoot',
        rows: [
          {
            duration: '1 hour',
            price: '€230',
            includes: '120 high resolution raw photos, 30 professionally edited photos, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€360',
            includes: '240 high resolution raw photos, 60 professionally edited photos, 2 reels, 7 spots',
          },
        ],
      },
      {
        id: 'couples-casual-photoshoot',
        title: 'Couples Casual Santorini Photoshoot',
        rows: [
          {
            duration: '1 hour',
            price: '€290 / couple',
            includes: '120 high resolution raw photos, 30 professionally edited photos, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€460 / couple',
            includes: '240 high resolution raw photos, 60 professionally edited photos, 2 reels, 7 spots',
          },
        ],
      },
      {
        id: 'group-casual-photoshoot',
        title: 'Group Casual Santorini Photoshoot',
        description: 'Photos delivered per person.',
        rows: [
          {
            duration: '1 hour',
            price: '€145 / person',
            includes:
              '120 high resolution raw photos per person, 30 professionally edited photos per person, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€230 / person',
            includes:
              '240 high resolution raw photos per person, 60 professionally edited photos per person, 2 reels, 7 spots',
          },
        ],
      },
      {
        id: 'family-casual-photoshoot',
        title: 'Family Casual Santorini Photoshoot',
        rows: [],
        note: 'Family photoshoots are available upon request. Price depends on the number of family members - message us and we will tailor the session for you.',
      },
    ],
    deliveryNote: DELIVERY,
  },
  {
    id: 'santorini-tour',
    productAlias: 'hiddengems',
    slug: 'santorini-photoshooting-tour',
    kicker: 'Photoshooting Tour',
    title: 'Santorini Photoshooting Tour',
    pageTitle: 'Santorini Photoshooting Tour',
    cardDescription:
      "Explore the island's hidden gems with a local guide while we shoot the most breathtaking spots.",
    intro:
      'A private Santorini photoshooting tour through hidden gems, elegant villages and breathtaking caldera-view locations. Guided by a local, this experience combines island discovery with professional photography. The route includes Santorini Heart, Megalochori and three secret locations, with local insight, posing guidance and a luxury travel feeling.',
    images: ['/img/santorini-tour.webp'],
    badge: 'Most Loved',
    // NB: product alias is 'hiddengems'; the single option's alias is 'santorin'.
    fromPrice: 590,
    audience: [COUPLE, GROUP, FAMILY],
    included: [
      'Local tour guide',
      'Private Santorini route',
      'Santorini Heart',
      'Megalochori',
      'Three secret locations',
      'Hidden gems of the island',
      'Caldera and volcano-view spots',
      'Island history and local insight',
      'Pick-up and drop-off',
      'Posing guidance',
      'Drinks and bites',
      '200 high resolution raw photos',
      '50 professionally edited photos',
      '3 reels',
      'Optional flying dress add-on',
    ],
    packages: [
      {
        id: 'santorini-photoshooting-tour-for-2-people',
        title: 'Santorini Photoshooting Tour for 2 People',
        rows: [
          {
            guests: '2 people',
            duration: '3 hours',
            price: '€590',
            includes:
              'Private Santorini photoshooting tour, 200 high resolution raw photos, 50 professionally edited photos, 3 reels',
          },
        ],
      },
      {
        id: 'santorini-photoshooting-tour-for-3-7-people',
        title: 'Santorini Photoshooting Tour for 3-7 People',
        rows: [
          {
            guests: '3-7 people',
            duration: '3 hours',
            price: '€790',
            includes:
              'Private Santorini photoshooting tour, 300 high resolution raw photos, 70 professionally edited photos, 4 reels',
          },
        ],
      },
    ],
    deliveryNote: DELIVERY,
    addOns: ['Flying dress add-on'],
  },
  {
    id: 'love-story',
    productAlias: 'yourlove',
    slug: 'santorini-proposal-honeymoon-photoshoot',
    kicker: 'Your Love Story',
    title: 'Your Love Story',
    pageTitle: 'Santorini Proposal & Honeymoon Photoshoot',
    cardDescription:
      "For couples celebrating their love story in one of Santorini's most breathtaking settings.",
    intro:
      'A tailor-made Santorini proposal and honeymoon photoshoot designed for romantic moments. Ideal for secret proposals, honeymoons, anniversaries, after-wedding sessions and private celebrations. The experience includes an exclusive caldera or volcano-view location, elegant romantic details, posing guidance and refined photography direction.',
    images: ['/img/love-story.webp', '/img/love-story-2.webp'],
    fromPrice: 290,
    audience: [COUPLE],
    idealFor: [
      'Secret proposals',
      'Honeymoon photoshoots',
      'Honeymoon photoshoot tours',
      'Anniversaries',
      'After-wedding photoshoots',
      'Romantic celebrations',
    ],
    included: [
      'Secret proposal setup, when needed',
      'Exclusive caldera or volcano-view location',
      'Champagne and fruit basket',
      'Flower bouquet upon request',
      'Love petals upon request',
      'Pick-up and drop-off',
      'Posing guidance',
      'High resolution raw photos',
      'Professionally edited photos',
      'Optional flying dress experience',
    ],
    packages: [
      {
        id: 'secret-proposal-photoshoot',
        title: 'Secret Proposal Photoshoot',
        rows: [
          {
            price: 'From €290',
            includes:
              'Posing guidance, proposal setup, private photoshoot, exclusive view location, 100 high resolution raw photos, 30 professionally edited photos',
          },
        ],
      },
      {
        id: 'honeymoon-photoshoot',
        aliases: ['anniversary-photoshoot'],
        title: 'Honeymoon & Anniversary Photoshoot',
        rows: [
          {
            duration: '1 hour',
            price: '€290',
            includes: 'Posing guidance, 120 high resolution raw photos, 30 professionally edited photos, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€460',
            includes: 'Posing guidance, 240 high resolution raw photos, 60 professionally edited photos, 2 reels, 7 spots',
          },
        ],
      },
      {
        id: 'honeymoon-photoshoot-tour',
        title: 'Honeymoon Photoshoot Tour',
        description:
          'A private honeymoon photoshoot tour for two, combining romantic Santorini locations with local island discovery and professional photography.',
        rows: [
          {
            guests: '2 people',
            duration: '3 hours',
            price: '€590',
            includes:
              'Posing guidance, champagne and bites, private Santorini photoshoot tour, local tour guide, Santorini Heart, Megalochori, three secret locations, 200 high resolution raw photos, 50 professionally edited photos, 3 reels',
          },
        ],
      },
      {
        id: 'after-wedding-photoshoot',
        title: 'After-Wedding Photoshoot',
        rows: [
          {
            duration: '1 hour',
            price: '€290',
            includes: 'Posing guidance, 120 high resolution raw photos, 30 professionally edited photos, 1 reel, 3 spots',
          },
          {
            duration: '2 hours',
            price: '€460',
            includes: 'Posing guidance, 240 high resolution raw photos, 60 professionally edited photos, 2 reels, 7 spots',
          },
        ],
      },
    ],
    deliveryNote: DELIVERY,
    addOns: [
      'Flying dress',
      'Extra flowers',
      'Premium romantic setup',
      'Extra professionally edited photos',
      'Extra shooting time',
      'Additional reels',
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** The primary (hero) image for an experience. */
export function primaryImage(exp: Experience): string {
  return exp.images[0];
}

/** Look up an experience by its URL slug. */
export function experienceBySlug(slug: string): Experience | undefined {
  return experiences.find((e) => e.slug === slug);
}

/** Look up an experience by its LinkTwist product alias. */
export function experienceByAlias(alias: string): Experience | undefined {
  return experiences.find((e) => e.productAlias === alias);
}

/**
 * Maps each package group's rows (by index) to the LinkTwist option that prices
 * them, so the package tables can render live prices. `pax` is how many
 * participants the row's price represents (a "/ couple" row is priced for 2; a
 * tour row for its group size). Groups omitted here (Family, Honeymoon Tour)
 * have no matching option and keep their static price.
 */
export const packageRowOptions: Record<string, { alias: string; pax: number }[]> = {
  'solo-flying-dress-photoshoot': [
    { alias: 'soloflyi', pax: 1 },
    { alias: 'soloflyi1', pax: 1 },
  ],
  'couples-flying-dress-photoshoot': [
    { alias: 'couplesf', pax: 2 },
    { alias: 'couplesf1', pax: 2 },
  ],
  'group-flying-dress-photoshoot': [
    { alias: 'groupfly', pax: 1 },
    { alias: 'groupfly1', pax: 1 },
  ],
  'solo-casual-photoshoot': [
    { alias: 'solocasu', pax: 1 },
    { alias: 'solocasu1', pax: 1 },
  ],
  'couples-casual-photoshoot': [
    { alias: 'couplesc', pax: 2 },
    { alias: 'couplesc1', pax: 2 },
  ],
  'group-casual-photoshoot': [
    { alias: 'groupcas', pax: 1 },
    { alias: 'groupcas1', pax: 1 },
  ],
  'santorini-photoshooting-tour-for-2-people': [{ alias: 'santorin', pax: 2 }],
  'santorini-photoshooting-tour-for-3-7-people': [{ alias: 'santorin', pax: 3 }],
  'secret-proposal-photoshoot': [{ alias: 'secretpr', pax: 1 }],
  'honeymoon-photoshoot': [
    { alias: 'honeymoo', pax: 1 },
    { alias: 'honeymoo1', pax: 1 },
  ],
  'after-wedding-photoshoot': [
    { alias: 'afterwed', pax: 1 },
    { alias: 'afterwed1', pax: 1 },
  ],
};
